import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePWABanner } from "@/hooks/usePWABanner";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { User, Briefcase } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { userApi } from "@/services/userService";
import { useQuery } from "@tanstack/react-query";
import { SignupRequestDto } from "@/services/authService";
import { projectApi } from "@/services/projectService";
import { InvitationResponse } from "@/types/invitation";
import { passwordSchema, getPasswordHelperText } from "@/lib/passwordValidation";
import PWAInstallBanner from "@/components/mobile/PWAInstallBanner";
import { cn } from "@/lib/utils";

const signupSchema = z.object({
  firstName: z.string().min(1, "First Name is required"),
  lastName: z.string().min(1, "Last Name is required"),
  company: z.string().min(1, "Company Name is required"),
  password: passwordSchema,
  confirmPassword: passwordSchema,
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type SignupFormValues = z.infer<typeof signupSchema>;

const Signup = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { signup, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [invitationId, setInvitationId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const { isBannerVisible } = usePWABanner();

  // Get invite token from URL
  const searchParams = new URLSearchParams(location.search);
  const inviteToken = searchParams.get("invite");
  const projectToken = searchParams.get("projectInvite");

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      company: "",
      password: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!inviteToken && !projectToken) {
      // No invite token, redirect to login
      toast({
        title: "Invalid invitation",
        description: "No invitation token was provided",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }
  }, [inviteToken, projectToken, navigate, toast, form]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // If user has just signed up through an invitation, redirect to accept page
      const currentToken = inviteToken || projectToken;
      if (currentToken) {
        navigate(`/accept-invitations?token=${encodeURIComponent(currentToken)}`);
      } else {
        navigate("/");
      }
    }
  }, [isAuthenticated, navigate, inviteToken, projectToken]);

  useEffect(() => {
    form.reset({
      ...form.getValues(),
    });
  }, [form]);

  const onSubmit = async (data: SignupFormValues) => {
    // Password match and min length are now handled by Zod schema

    const currentToken = inviteToken || projectToken;
    if (!currentToken) {
      toast({
        title: "Error",
        description: "Invitation token is missing",
        variant: "destructive",
      });
      return;
    }

    const signupData: SignupRequestDto = {
      token: currentToken,
      firstName: data.firstName,
      lastName: data.lastName,
      companyName: data.company,
      password: data.password,
      ...(projectToken && {
        projectId,
      }),
    };

    setIsLoading(true);
    try {
      const success = await signup(signupData);
      if (success) {
        // AuthContext's onSuccess handles token and user state
        // useEffect will redirect due to isAuthenticated change
      }
    } catch (error) {
      // Error handled by AuthContext
    } finally {
      setIsLoading(false);
    }
  };

  const { data, status } = useQuery<InvitationResponse, Error>({
    queryKey: ["validateInvite", inviteToken, projectToken],
    queryFn: async (): Promise<InvitationResponse> => {
      if (projectToken) {
        const response = await projectApi.validateProjectInvite(projectToken);
        return {
          type: "project",
          isValid: response.isValid,
          invitationId: response.invitationId,
          email: response.email,
          role: response.role,
          project: response.project,
          projectId: response.projectId,
          invitedBy: response.invitedBy,
        };
      }

      if (inviteToken) {
        const response = await userApi.validateInvite(inviteToken);
        return {
          type: "global",
          isValid: true,
          invitationId: response.invitationId,
          email: response.email,
          role: response.role,
          invitedBy: response.invitedBy,
        };
      }

      throw new Error("No invitation token provided");
    },
    enabled: !!inviteToken || !!projectToken,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) {
      setInvitationId(data.invitationId);
      if (data.type === "project") {
        // Project invite
        setProjectId(data.projectId);
      }
    }

    if (status === "error") {
      toast({
        title: "Invalid invitation",
        description: "The invitation link is invalid or has expired",
        variant: "destructive",
      });
      navigate("/login");
    }
  }, [status, data, navigate, toast]);

  // Show loading screen
  if (status === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Validating Invitation</CardTitle>
            <CardDescription>
              Please wait while we validate your invitation...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show invalid invite screen
  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50 p-4">
        <Card className="w-full max-w-md border-red-500">
          <CardHeader className="text-center">
            <CardTitle className="text-red-700">Invalid Invitation</CardTitle>
            <CardDescription>
              The invitation link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            <button
              onClick={() => navigate("/login")}
              className="text-sm font-medium text-red-600 underline"
            >
              Go to Login
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* PWA Install Banner */}
      <PWAInstallBanner />
      
      <div className={cn(
        "min-h-screen flex items-center justify-center bg-gray-50 p-4",
        isBannerVisible ? "pt-20" : "pt-4"
      )}>
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center">
              <img src="/logo.svg" alt="Quantum Warehouse Solutions logo" />
            </div>
            <h1 className="mt-4 text-3xl font-bold text-gray-900">
              Complete Your Registration
            </h1>
            <p className="mt-2 text-gray-600">
              Create your account to get started
            </p>
          </div>

        <Card>
          <CardHeader>
            <CardTitle>Sign Up</CardTitle>
            <CardDescription>
              Fill in your details to create your account
            </CardDescription>
          </CardHeader>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent className="space-y-4">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium">
                  First Name<span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex items-center space-x-2 mt-1">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <input
                    {...form.register("firstName")}
                    placeholder="John"
                    className="flex-1 border rounded px-3 py-2"
                  />
                </div>
                {form.formState.errors.firstName && (
                  <p className="text-sm text-red-500 mt-1">
                    {form.formState.errors.firstName.message}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium">
                  Last Name<span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex items-center space-x-2 mt-1">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <input
                    {...form.register("lastName")}
                    placeholder="Doe"
                    className="flex-1 border rounded px-3 py-2"
                  />
                </div>
                {form.formState.errors.lastName && (
                  <p className="text-sm text-red-500 mt-1">
                    {form.formState.errors.lastName.message}
                  </p>
                )}
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium">
                  Company Name<span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex items-center space-x-2 mt-1">
                  <Briefcase className="w-4 h-4 text-muted-foreground" />
                  <input
                    {...form.register("company")}
                    placeholder="Your Company"
                    className="flex-1 border rounded px-3 py-2"
                  />
                </div>
                {form.formState.errors.company && (
                  <p className="text-sm text-red-500 mt-1">
                    {form.formState.errors.company.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium">Password</label>
                <PasswordInput
                  {...form.register("password")}
                  placeholder="Enter your password"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {getPasswordHelperText()}
                </p>
                {form.formState.errors.password && (
                  <p className="text-sm text-red-500 mt-1">
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium">
                  Confirm Password
                </label>
                <PasswordInput
                  {...form.register("confirmPassword")}
                  placeholder="Confirm your password"
                  className="mt-1"
                />
                {form.formState.errors.confirmPassword && (
                  <p className="text-sm text-red-500 mt-1">
                    {form.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </CardContent>

            <CardFooter className="flex justify-between">
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Creating Account..." : "Complete Signup"}
              </Button>
            </CardFooter>
          </form>
        </Card>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>
            Already have an account?{" "}
            <a href="/login" className="text-primary hover:underline">
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default Signup;
